import { SetStateAction, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from '../lib/interfaces/utils';

interface UsePaginationOptions {
  pageSize?: number;
  pageParamName?: string;
}

interface UsePaginationReturn {
  pagination: Pagination;
  isValidPagination: boolean;
  handlePaginationChange: (newPagination: SetStateAction<Pagination>) => void;
  resetPagination: () => void;
  resetPaginationOnSearchParamsChange: (searchParams: URLSearchParams) => URLSearchParams;
}

const isValidPageParam = (pageParam: string | null): boolean => {
  const raw = Number(pageParam);
  return Number.isInteger(raw) && raw > 0;
};

const getPageIndexFromURL = (searchParams: URLSearchParams, pageParamName: string): number => {
  const pageParam = searchParams.get(pageParamName);
  const isValid = isValidPageParam(pageParam);
  return isValid ? Number(pageParam) - 1 : 0;
};

/**
 *
 * @param pageSize - The page size to use
 * @param pageParamName - The page param name to use
 * @returns The pagination object and the functions to handle the pagination
 */
export const usePagination = ({ pageSize = 10, pageParamName = 'page' }: UsePaginationOptions): UsePaginationReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageIndex = getPageIndexFromURL(searchParams, pageParamName);
  const pageParam = searchParams.get(pageParamName);
  const isValidPagination = isValidPageParam(pageParam);

  const pagination: Pagination = {
    pageIndex,
    pageSize
  };

  useEffect(() => {
    if (isValidPageParam(pageParam)) return;

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set(pageParamName, '1');
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams, pageParamName, pageParam]);

  /**
   * Handle the pagination change by setting the search params
   * @param newPagination - The new pagination to set
   */
  const handlePaginationChange = (newPagination: SetStateAction<Pagination>) => {
    const next = typeof newPagination === 'function' ? newPagination(pagination) : newPagination;

    if (next.pageIndex === pagination.pageIndex) return;

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(pageParamName, String(next.pageIndex + 1));
      return params;
    });
  };

  /**
   * Reset the pagination by setting the pagination and calling setSearchParams
   */

  const resetPagination = () => {
    if (searchParams.get(pageParamName) === '1') return;

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(pageParamName, '1');
      return params;
    });
  };

  /**
   * Reset the pagination on search params change without calling setSearchParams
   * @param searchParams - The search params to reset the pagination on
   * @returns The search params with the pagination reset
   */
  const resetPaginationOnSearchParamsChange = (params: URLSearchParams) => {
    params.set(pageParamName, '1');
    return params;
  };

  return {
    pagination,
    isValidPagination,
    handlePaginationChange,
    resetPagination,
    resetPaginationOnSearchParamsChange
  };
};
